import { useCallback, useMemo, type ReactNode } from "react";

import { graphql, useLazyLoadQuery, useRefetchableFragment } from "react-relay";

import { toGridPagination } from "app/components/table/StackletGrid";
import { useCursorPagination } from "app/hooks/grid";

import { GroupedUsersGrid } from "./GroupedUsersGrid";

import type { RoleUsersGrouping_query$key } from "./__generated__/RoleUsersGrouping_query.graphql";
import type { RoleUsersGroupingQuery } from "./__generated__/RoleUsersGroupingQuery.graphql";
import type { RoleUsersGroupingRefetchQuery } from "./__generated__/RoleUsersGroupingRefetchQuery.graphql";
import type { BucketRow } from "./GroupedUsersGrid";
import type { RetainedSearch } from "./userGrouping";
import type { CursorVariables } from "app/hooks/grid";

/** Roles shown per page in the bucket list. */
const ROLE_PAGE = 25;
/** Selectable page sizes for the bucket pager. */
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

interface Props extends RetainedSearch {
  /** The group-by dropdown, for the grid toolbar. */
  toolbarActions?: ReactNode;
}

/** Lists roles a page at a time; expanding one shows the users who hold it. */
export function RoleUsersGrouping({
  toolbarActions,
  initialSearchTerm,
  onSearchTermChange,
}: Props) {
  const queryData = useLazyLoadQuery<RoleUsersGroupingQuery>(
    graphql`
      query RoleUsersGroupingQuery($first: Int) {
        ...RoleUsersGrouping_query @arguments(first: $first)
      }
    `,
    { first: ROLE_PAGE },
  );

  const [data, refetch] = useRefetchableFragment<
    RoleUsersGroupingRefetchQuery,
    RoleUsersGrouping_query$key
  >(
    graphql`
      fragment RoleUsersGrouping_query on Query
      @argumentDefinitions(
        first: { type: "Int" }
        last: { type: "Int" }
        after: { type: "String" }
        before: { type: "String" }
      )
      @refetchable(queryName: "RoleUsersGroupingRefetchQuery") {
        roles(first: $first, last: $last, after: $after, before: $before) {
          edges {
            node {
              id
              name
              system
            }
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
            total
          }
        }
      }
    `,
    queryData,
  );

  const rows = useMemo<BucketRow[]>(
    () =>
      (data.roles.edges ?? [])
        .map((edge) => edge.node)
        .filter((node): node is NonNullable<typeof node> => Boolean(node))
        .map((node) => ({
          id: node.id,
          label: node.name,
          // Roles have no dedicated details page.
          to: null,
          memberScope: { kind: "role" as const, roleName: node.name },
          detail: node.system ? "System role" : null,
        })),
    [data.roles.edges],
  );

  const onNavigate = useCallback(
    (variables: CursorVariables) =>
      refetch(variables, { fetchPolicy: "network-only" }),
    [refetch],
  );

  const cursor = useCursorPagination({
    pageInfo: data.roles.pageInfo,
    rowCount: rows.length,
    defaultPageSize: ROLE_PAGE,
    onNavigate,
  });

  const { hasNextPage, hasPreviousPage, total } = data.roles.pageInfo;
  const pagination = toGridPagination(
    cursor,
    { hasNextPage, hasPreviousPage, total },
    { unit: "role", pageSizeOptions: PAGE_SIZE_OPTIONS },
  );

  return (
    <GroupedUsersGrid
      buckets={rows}
      initialSearchTerm={initialSearchTerm}
      loading={cursor.isPending}
      onSearchTermChange={onSearchTermChange}
      pagination={pagination}
      searchKind="role"
      tableId="grouped-users-roles"
      toolbarActions={toolbarActions}
      unit="role"
    />
  );
}
